"use client";

import Image from "next/image";
import { motion, useScroll, useTransform, type Variants } from "framer-motion";

import LayoutWrapper from "@/components/marketing/linear/ui/layout-wrapper";
import { usePlatformAuth } from "@/hooks/use-platform-auth";

const EASE_OUT_EXPO = [0.22, 1, 0.36, 1] as const;

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: EASE_OUT_EXPO },
  }),
};

const STORY_BLOCKS = [
  {
    title: "Notes should feel alive.",
    body: "Inora began with a simple belief: writing and thinking deserve an interface as intentional as the ideas inside them. Not a document dump. Not a generic editor.",
  },
  {
    title: "Structure without friction.",
    body: "We build modular notebooks where sections, layouts, and interactions stay predictable. You compose faster because the system respects your rhythm.",
  },
  {
    title: "Craft over noise.",
    body: "Every surface is designed to stay calm under pressure: long sessions, dense notebooks, and the quiet focus required to do meaningful work.",
  },
];

const ABOUT_IMAGES = [
  {
    src: "/recentnotes-main.png",
    alt: "Inora home with recent notes and fan-card folders",
    width: 2880,
    height: 1565,
  },
  {
    src: "/hero.webp",
    alt: "Inora interactive notebook editor",
    width: 2879,
    height: 1576,
  },
] as const;

const ABOUT_IMAGE_CLASS =
  "block h-auto w-full max-w-full object-contain object-center";

export function AboutPageContent() {
  const { isAuthenticated } = usePlatformAuth();
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 400], [0, 40]);
  const heroOpacity = useTransform(scrollY, [0, 320], [1, 0.72]);

  const inAppShell = isAuthenticated;
  const heroMotionStyle = inAppShell ? undefined : { y: heroY, opacity: heroOpacity };
  const scrollReveal = inAppShell
    ? {
        initial: { opacity: 0, y: 18 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.55, ease: EASE_OUT_EXPO },
      }
    : {
        initial: { opacity: 0, y: 32 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-80px" },
        transition: { duration: 0.8, ease: EASE_OUT_EXPO },
      };

  return (
    <LayoutWrapper>
        <motion.section
          style={heroMotionStyle}
          className="mx-auto grid max-w-6xl gap-16 pb-28 pt-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] md:gap-20 md:pt-6"
        >
          <motion.div
            initial="hidden"
            animate="visible"
            className="flex flex-col justify-end"
          >
            <motion.p
              custom={0}
              variants={fadeUp}
              className="mb-6 text-[11px] font-medium uppercase tracking-[0.22em] text-neutral-500"
            >
              About Inora
            </motion.p>
            <motion.h1
              custom={1}
              variants={fadeUp}
              className="max-w-xl text-[clamp(2.5rem,6vw,4.25rem)] font-semibold leading-[1.02] tracking-[-0.03em] text-white"
            >
              A new species of notebook.
            </motion.h1>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            className="flex flex-col justify-end space-y-6 text-[17px] leading-[1.75] text-neutral-400"
          >
            <motion.p custom={2} variants={fadeUp}>
              Software for thinking is at an inflection point. The tools we use
              should feel as considered as the work they hold: immersive when
              you need depth, invisible when you need focus.
            </motion.p>
            <motion.p custom={3} variants={fadeUp}>
              Inora is our answer: an interactive notebook platform where
              structure, motion, and editing stay in harmony. Not louder. Not
              busier. More alive.
            </motion.p>
          </motion.div>
        </motion.section>

        <motion.div
          {...scrollReveal}
          className="relative mx-auto mb-28 max-w-6xl rounded-2xl border border-white/[0.06] bg-[#060606]"
        >
          <Image
            src={ABOUT_IMAGES[0].src}
            alt={ABOUT_IMAGES[0].alt}
            width={ABOUT_IMAGES[0].width}
            height={ABOUT_IMAGES[0].height}
            className={ABOUT_IMAGE_CLASS}
            sizes="(max-width: 1200px) 100vw, 1200px"
            priority
          />
        </motion.div>

        <section className="mx-auto max-w-3xl space-y-24 pb-32">
          {STORY_BLOCKS.map((block, index) => (
            <motion.article
              key={block.title}
              {...(inAppShell
                ? {
                    initial: { opacity: 0, y: 16 },
                    animate: { opacity: 1, y: 0 },
                    transition: {
                      duration: 0.5,
                      delay: index * 0.04,
                      ease: EASE_OUT_EXPO,
                    },
                  }
                : {
                    initial: { opacity: 0, y: 24 },
                    whileInView: { opacity: 1, y: 0 },
                    viewport: { once: true, margin: "-60px" },
                    transition: { duration: 0.65, delay: index * 0.05 },
                  })}
              className="space-y-4"
            >
              <h2 className="text-2xl font-medium tracking-tight text-white md:text-3xl">
                {block.title}
              </h2>
              <p className="text-[16px] leading-[1.8] text-neutral-400">
                {block.body}
              </p>
            </motion.article>
          ))}
        </section>

        <motion.div
          {...(inAppShell
            ? {
                initial: { opacity: 0, y: 18 },
                animate: { opacity: 1, y: 0 },
                transition: { duration: 0.55, ease: EASE_OUT_EXPO },
              }
            : {
                initial: { opacity: 0, y: 32 },
                whileInView: { opacity: 1, y: 0 },
                viewport: { once: true },
                transition: { duration: 0.8 },
              })}
          className="relative mx-auto mb-20 max-w-6xl rounded-2xl border border-white/[0.06] bg-[#060606]"
        >
          <Image
            src={ABOUT_IMAGES[1].src}
            alt={ABOUT_IMAGES[1].alt}
            width={ABOUT_IMAGES[1].width}
            height={ABOUT_IMAGES[1].height}
            className={ABOUT_IMAGE_CLASS}
            sizes="(max-width: 1200px) 100vw, 1200px"
          />
        </motion.div>

        <motion.section
          {...(inAppShell
            ? {
                initial: { opacity: 0, y: 14 },
                animate: { opacity: 1, y: 0 },
                transition: { duration: 0.5, ease: EASE_OUT_EXPO },
              }
            : {
                initial: { opacity: 0, y: 20 },
                whileInView: { opacity: 1, y: 0 },
                viewport: { once: true },
              })}
          className="mx-auto max-w-2xl pb-8 text-center"
        >
          <p className="text-[15px] leading-relaxed text-neutral-500">
            We are building the notebook we wanted to exist: calm, capable, and
            worthy of long-form creative work.
          </p>
        </motion.section>
    </LayoutWrapper>
  );
}
